import React from 'react'

import appIconGooglePlay from '../../../assets/svg/google-play-badge.svg'
import appIconAppStore from '../../../assets/svg/app-store-badge.svg'
import hausmartLogo from '../../../assets/svg/Hausmart_logo_text.svg'
import LangPicker from '../LangPicker/LangPicker'
import { FormattedMessage } from 'react-intl'
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaShopify } from 'react-icons/fa'
import {
  RAMBLA_URL,
  IOS_APP_URL,
  ANDROID_APP_URL,
  SHOPIFY_APP_STORE_URL,
  HAUSMART_IOS_APP_URL,
  HAUSMART_ANDROID_APP_URL,
  ZEROTOUCH_IOS_APP_URL,
  ZEROTOUCH_ANDROID_APP_URL,
  HAUSMART_SOCIAL
} from '../../../constants/appLinks'
import {
  MainContainer,
  FooterInner,
  TopSection,
  BrandColumn,
  Logo,
  Description,
  LinksCluster,
  LinkColumn,
  ColumnTitle,
  LinkFooter,
  LinkFooterExternal,
  DownloadColumn,
  AppGroup,
  AppGroupsGrid,
  AppGroupLabel,
  StoresLink,
  AppLink,
  AppMobileIcon,
  ShopifyLink,
  ShopifyCopy,
  ShopifyEyebrow,
  ShopifyName,
  BottomSection,
  LegalLinks,
  LegalDot,
  SocialRow,
  SocialLink,
  CircleIcon,
  LangPickerWrap
} from './FooterStyledComponents'

interface Props {
  animate: boolean
  isZeroTouch: boolean
}

const Footer = React.memo(({ animate, isZeroTouch = false, ...rest }: Props) => {
  const hausmartIosUrl = isZeroTouch ? ZEROTOUCH_IOS_APP_URL : HAUSMART_IOS_APP_URL
  const hausmartAndroidUrl = isZeroTouch ? ZEROTOUCH_ANDROID_APP_URL : HAUSMART_ANDROID_APP_URL

  return (
    <MainContainer delay={100} initialPose={'hidden'} pose={animate && 'visible'} {...rest}>
      <FooterInner>
        <TopSection>
          <BrandColumn>
            <Logo src={hausmartLogo} alt="HausMart logo" />
            <Description>
              <FormattedMessage id={isZeroTouch ? 'footer.zeroTouchDescription' : 'footer.hausmartDescription'} />
            </Description>
          </BrandColumn>

          <LinksCluster>
            <LinkColumn>
              <ColumnTitle>
                <FormattedMessage id="footer.help" />
              </ColumnTitle>
              <LinkFooter to="/hausmart/faq">
                <FormattedMessage id="footer.faq" />
              </LinkFooter>
            </LinkColumn>

            <LinkColumn>
              <ColumnTitle>
                <FormattedMessage id="footer.contact" />
              </ColumnTitle>
              <LinkFooterExternal href="mailto:info@hausmart.com">
                <FormattedMessage id="footer.emailUs" />
              </LinkFooterExternal>
            </LinkColumn>

            <LinkColumn>
              <ColumnTitle>
                <FormattedMessage id="footer.welcomePage" />
              </ColumnTitle>
              <LinkFooterExternal href={RAMBLA_URL} target="_blank" rel="noopener noreferrer">
                <FormattedMessage id="footer.ramblaIo" />
              </LinkFooterExternal>
            </LinkColumn>
          </LinksCluster>

          <DownloadColumn>
            <ColumnTitle>
              <FormattedMessage id="footer.downloadApp" />
            </ColumnTitle>
            <AppGroupsGrid>
            <AppGroup>
              <AppGroupLabel>
                <FormattedMessage id="footer.hausmartApp" />
              </AppGroupLabel>
              <StoresLink>
                <AppLink href={hausmartIosUrl} target="_blank" rel="noopener noreferrer" aria-label="Get HausMart for iOS">
                  <AppMobileIcon src={appIconAppStore} alt="App Store" />
                </AppLink>
                <AppLink
                  href={hausmartAndroidUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get HausMart for Android"
                >
                  <AppMobileIcon src={appIconGooglePlay} alt="Google Play" />
                </AppLink>
              </StoresLink>
            </AppGroup>
            <AppGroup>
              <AppGroupLabel>
                <FormattedMessage id="footer.ramblaApp" />
              </AppGroupLabel>
              <StoresLink>
                <AppLink href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" aria-label="Get Rambla for iOS">
                  <AppMobileIcon src={appIconAppStore} alt="App Store" />
                </AppLink>
                <AppLink
                  href={ANDROID_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Rambla for Android"
                >
                  <AppMobileIcon src={appIconGooglePlay} alt="Google Play" />
                </AppLink>
                <ShopifyLink href={SHOPIFY_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <FaShopify />
                  <ShopifyCopy>
                    <ShopifyEyebrow>
                      <FormattedMessage id="footer.availableOn" />
                    </ShopifyEyebrow>
                    <ShopifyName>
                      <FormattedMessage id="footer.shopify" />
                    </ShopifyName>
                  </ShopifyCopy>
                </ShopifyLink>
              </StoresLink>
            </AppGroup>
            </AppGroupsGrid>
          </DownloadColumn>
        </TopSection>

        <BottomSection>
          <LangPickerWrap>
            <LangPicker />
          </LangPickerWrap>
          <LegalLinks>
            <LinkFooter to="/terms/1/Term-of-service">
              <FormattedMessage id="footer.termsOfService" />
            </LinkFooter>
            <LegalDot>·</LegalDot>
            <LinkFooter to="/terms/4/Privacy-Policy">
              <FormattedMessage id="footer.privacyPolicy" />
            </LinkFooter>
          </LegalLinks>
          <SocialRow>
            <SocialLink href={HAUSMART_SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="HausMart on Facebook">
              <CircleIcon>
                <FaFacebookF />
              </CircleIcon>
            </SocialLink>
            <SocialLink href={HAUSMART_SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="HausMart on Instagram">
              <CircleIcon>
                <FaInstagram />
              </CircleIcon>
            </SocialLink>
            <SocialLink href={HAUSMART_SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="HausMart on LinkedIn">
              <CircleIcon>
                <FaLinkedinIn />
              </CircleIcon>
            </SocialLink>
          </SocialRow>
        </BottomSection>
      </FooterInner>
    </MainContainer>
  )
})

Footer.displayName = 'Footer'

export default Footer
